import { useState } from 'preact/hooks';
import { pemEncode } from '../../core/bytes/encoding';
import { chainFromHost } from '../../core/chain/wmcc';
import type { DiscoveredChain } from '../../core/chain/types';
import { transport } from '../state/transport';
import { ErrorBox } from './ErrorBox';
import { Icon } from './Icon';
import { isDesktop, peerChain } from '../state/desktop';
import { parseCertificate } from '../../core/x509/parse';

/** The chain a server presents, read by the desktop shell's own TLS handshake. Accepts "host" or "host:port". */
async function chainFromServer(input: string): Promise<DiscoveredChain> {
  const m = /^\s*(?:https?:\/\/)?\[?([^\]\/:\s]+)\]?(?::(\d{1,5}))?\/?\s*$/.exec(input);
  if (!m) throw new Error('Enter a host name, optionally with a port (host:port).');
  const host = m[1]!;
  const port = m[2] ? Number(m[2]) : undefined;
  const chain = await peerChain(host, port);
  const subjects = chain.map((d) => { try { return parseCertificate(d).subject; } catch { return '(unparseable)'; } });
  const last = chain[chain.length - 1]!;
  let endsAtRoot = false;
  try { const p = parseCertificate(last); endsAtRoot = p.subject === p.issuer; } catch { /* leave false */ }
  return { chain, subjects, endsAtRoot, source: 'server', detail: `served by ${host}:${port ?? 443} in the TLS handshake` };
}

/** "Fetch from a host" — the desktop app connects itself; the web page asks whatsmychaincert.com. */
export function HostFetch({ onChain, placeholder = 'example.com or host:port' }: { onChain: (pem: string, chain: DiscoveredChain) => void; placeholder?: string }) {
  const desktop = isDesktop();
  const [host, setHost] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>();
  const run = async (e?: Event) => {
    e?.preventDefault();
    if (!host.trim()) return;
    setBusy(true);
    setErr(undefined);
    try {
      const r = desktop ? await chainFromServer(host) : await chainFromHost(host, transport);
      onChain(r.chain.map((d) => pemEncode(d)).join(''), r);
    } catch (ex) {
      setErr(ex);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div class="stack" style="gap:0.5rem">
      <form class="row" onSubmit={run}>
        <span class="small muted">or fetch a site's current certificate:</span>
        <input class="input input-sm mono" style="width:min(34ch, 100%)" value={host} onInput={(e) => setHost((e.target as HTMLInputElement).value)} placeholder={placeholder} aria-label="Host name" spellcheck={false} />
        <button type="submit" class="btn btn-sm" disabled={busy || !host.trim()} title={desktop ? "Connects to the server and reads the certificate chain it presents (TLS handshake, nothing verified)" : "Retrieves the server's certificate and its chain via whatsmychaincert.com (SSLMate)"}>
          <Icon name="download" /> {busy ? 'Fetching…' : 'Fetch from host'}
        </button>
      </form>
      {err !== undefined && <ErrorBox error={err} title="Could not fetch the certificate" />}
    </div>
  );
}
